import { getGenerativeModel } from 'firebase/ai';
import { ai } from '@/lib/firebase';

/**
 * Thin wrapper around Firebase AI Logic (client-side Gemini).
 *
 * This is intentionally scoped to SMALL, latency-sensitive calls:
 *   - query refinement suggestions in the command palette
 *   - short dossier-side summaries
 *
 * Anything that needs deep reasoning, graph traversal, or citations
 * goes through the server-side `/api/ask` pipeline instead (Gemini
 * primary on Vertex with Anthropic Claude as auto-failover backup).
 */
const model = getGenerativeModel(ai, { model: 'gemini-3-flash-preview' });

const SUGGEST_SYSTEM = {
  en:
    'You are a query refinement assistant for an intelligence analyst using a graph-native retrieval platform. ' +
    'Given partial user input, suggest 3 crisp, specific queries they might want to ask. ' +
    'Return ONLY a JSON array of 3 strings. No prose, no markdown.',
  fa:
    'شما یک دستیار پالایش پرس‌وجو برای یک تحلیل‌گر اطلاعاتی هستید که از یک پلتفرم بازیابی مبتنی بر گراف استفاده می‌کند. ' +
    'بر اساس ورودی جزئی کاربر، ۳ پرس‌وجوی دقیق و مشخص پیشنهاد دهید که ممکن است بخواهد بپرسد. ' +
    'فقط یک آرایهٔ JSON از ۳ رشته برگردانید. بدون متن توضیحی و بدون مارک‌داون.',
};

const SUMMARIZE_SYSTEM = {
  en: 'Summarize in 2-3 sentences, plain tone, no preamble.',
  fa: 'در ۲ تا ۳ جمله، با لحن ساده و بدون مقدمه خلاصه کنید.',
};

/**
 * Suggest 3 refined queries based on the user's partial input.
 * Returns [] on any parse or network failure — never throws.
 */
export async function suggestQueries(
  context: string,
  lang: 'en' | 'fa',
): Promise<string[]> {
  try {
    const result = await model.generateContent({
      systemInstruction: { role: 'system', parts: [{ text: SUGGEST_SYSTEM[lang] }] },
      contents: [{ role: 'user', parts: [{ text: context }] }],
      generationConfig: {
        responseMimeType: 'application/json',
        temperature: 0.4,
      },
    });
    const text = result.response.text();
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((s): s is string => typeof s === 'string').slice(0, 3);
  } catch {
    return [];
  }
}

/**
 * Short summary for dossier-side tldrs. Returns '' on failure.
 */
export async function summarizeText(
  text: string,
  lang: 'en' | 'fa',
): Promise<string> {
  try {
    const result = await model.generateContent({
      systemInstruction: {
        role: 'system',
        parts: [{ text: SUMMARIZE_SYSTEM[lang] }],
      },
      contents: [{ role: 'user', parts: [{ text }] }],
      generationConfig: { temperature: 0.4 },
    });
    return result.response.text().trim();
  } catch {
    return '';
  }
}
